import React from "react";
import classnames from "classnames";
import { Button, Col, Input, InputGroup, Label, Row } from "reactstrap";
import VariableArrayRow from "./VariableArrayRow";

/**
 * infotext building block (SCRUM-642, VariableField registry) — one intro
 * block (Title input + Description textarea) plus a repeatable list of
 * items ({subtitle, subdescription}) with add/remove buttons, wrapped in a
 * native <details>/<summary> whose summary text is the variable name.
 *
 * Byte-identical port of the legacy branches pinned by SCRUM-630/640:
 * - create: the IndexTemplateSelector.jsx infotext branch (wizard,
 *   ContentOptional only — the wizard's ContentRequired section has no
 *   infotext branch), pinned by
 *   tests/integration/pages/contentVariables.types.test.jsx.
 * - update: the the former Required host (.jsx) / the former Optional host (.jsx)
 *   branches (character-identical to each other), pinned by
 *   handleContentRequired.composites.test.jsx and
 *   handleContentOptional.types.test.jsx.
 *
 * Emits via `onChange(settingVar.variable, next)` where next is always the
 * FULL normalized canonical value
 * {title, description, items: [{subtitle, subdescription}]}.
 * Host reporting envelopes are step-4 (SCRUM-643) concerns, NOT implemented
 * here; the mapping is:
 * - wizard: onChangeVariableValue({target: {name, value: next}}) — the host
 *   mutates the Content entry in place and reports the whole array through
 *   props.onContentChange.
 * - update surfaces: setVariables(Variables.map(v => v.variable === name
 *   ? {...v, value: next} : v)) + props.onVariableChange(updatedVariable).
 *
 * Variant differences (variant="create" = wizard, "update" = both update
 * surfaces, which are character-identical):
 * - <details>: create = className "infotext-container" + bare
 *   data-collapsible, collapsed by default; update = "infotext-container
 *   mb-3" + open (the legacy key={index} is left to the host).
 * - item inputs: create binds value={item.subtitle} WITHOUT the || ''
 *   fallback (legacy wizard asymmetry, kept verbatim); update binds || ''.
 * - create only: the is-invalid wiring on Title/Description, driven by the
 *   optional validationError prop — dead in practice today (the wizard's
 *   validateForm skips ContentOptional vars, so the class resolves to '');
 *   preserved verbatim including the class="" it leaves on the textarea.
 * - summaryProps is spread on the <summary> (wizard dragHandleProps
 *   passthrough — infotext is in NEEDS_HANDLE_BY_TYPE); never consumed
 *   otherwise.
 *
 * Preserved quirks (do not clean up):
 * - render-time normalization (not load-time): non-object values (incl.
 *   "" and arrays — typeof [] === 'object' passes the guard) become the
 *   full default; missing/empty/non-array items render one blank row while
 *   the stored value stays untouched until the next edit re-materializes it.
 * - the FIRST edit emits the complete normalized object (untouched fields
 *   become "" and the default blank item is materialized).
 * - updateItem spreads {...it}: unknown item keys survive edits.
 * - removeItem has NO minimum guard: removing the last item emits
 *   items: [] and the next render resurrects a single blank row.
 * - index-based React keys on items; children stay mounted when collapsed
 *   (native <details> semantics — the wizard test reaches the inputs
 *   without expanding).
 * - exact strings: placeholders "Type the description…" /
 *   "Type the subtitle…" / "Type the subdescription…" (U+2026 ellipsis;
 *   the Title input has NO placeholder), bare name="title"/"description"
 *   (two infotext vars on one page would collide — today's behaviour,
 *   updateTopField keys off e.target.name), `${variable}[${i}].subtitle`
 *   name attrs (e2e-relied), the varible_container typo class, the
 *   &minus; remove button (not &times; — that's faq).
 */
export type InfotextItem = { subtitle?: string; subdescription?: string };
export type InfotextValue = { title: string; description: string; items: InfotextItem[] };

export type InfotextFieldProps = {
  /** a stored value that is not an object (e.g. "") renders the blank form */
  settingVar: { variable?: string; value?: Partial<InfotextValue> | string | null };
  onChange: (name: string | undefined, value: InfotextValue) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
  validationError?: React.ReactNode;
};

export default function InfotextField({
  settingVar,
  onChange,
  variant = "update",
  summaryProps = {},
  validationError,
}: InfotextFieldProps) {
  const fieldName = settingVar.variable;
  const isCreate = variant === "create";

  const value: InfotextValue =
    settingVar.value && typeof settingVar.value === "object"
      ? {
          title: settingVar.value.title || "",
          description: settingVar.value.description || "",
          items:
            Array.isArray(settingVar.value.items) && settingVar.value.items.length
              ? settingVar.value.items
              : [{ subtitle: "", subdescription: "" }],
        }
      : { title: "", description: "", items: [{ subtitle: "", subdescription: "" }] };

  const setValue = (next: InfotextValue) => onChange(fieldName, next);

  const updateTopField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValue({ ...value, [e.target.name]: e.target.value });

  const updateItem =
    (idx: number, key: keyof InfotextItem) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextItems = value.items.map((it, i) =>
      i === idx ? { ...it, [key]: e.target.value } : it
    );
    setValue({ ...value, items: nextItems });
  };

  const addItem = () =>
    setValue({ ...value, items: [...value.items, { subtitle: "", subdescription: "" }] });

  const removeItem = (idx: number) =>
    setValue({ ...value, items: value.items.filter((_, i) => i !== idx) });

  const subdescriptionArea = (i: number, text: string | undefined) => ({
    rows: 5,
    placeholder: "Type the subdescription…",
    value: text,
    onChange: updateItem(i, "subdescription"),
    name: `${fieldName}[${i}].subdescription`,
  });

  return (
    <details
      className={isCreate ? "infotext-container" : "infotext-container mb-3"}
      {...(isCreate ? { "data-collapsible": true } : { open: true })}
    >
      <summary
        className="varible_container form-control-label d-flex align-items-center"
        {...summaryProps}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {fieldName}
      </summary>

      <div className="pt-3">
        <Row className="varible_container">
          <Label md="2" className="form-control-label">Title</Label>
          <Col md="10">
            <Input
              type="text"
              name="title"
              value={value.title}
              onChange={updateTopField}
              className={isCreate ? (validationError ? "is-invalid" : "") : undefined}
            />
          </Col>
        </Row>

        <Row className="varible_container">
          <Label md="2" className="form-control-label">Description</Label>
          <Col md="10">
            <InputGroup className={classnames("input-group-merge")}>
              <textarea
                rows={5}
                cols={70}
                name="description"
                value={value.description}
                placeholder="Type the description…"
                onChange={updateTopField}
                className={isCreate ? (validationError ? "is-invalid" : "") : undefined}
              />
            </InputGroup>
          </Col>
        </Row>

        {value.items.map((item, i) => (
          // index key: controlled row, no row-local state (SCRUM-2183)
          <VariableArrayRow key={i} className="infotext-item" topRowClassName="mb-3 varible_container"
            topLabel={`Subtitle #${i + 1}`} onRemove={() => removeItem(i)}
            bottomRowClassName="varible_container" bottomLabel={`Subdescription #${i + 1}`}
            textarea={subdescriptionArea(i, isCreate ? item.subdescription : item.subdescription || "")}>
            <Col md="9">
              <Input type="text" value={isCreate ? item.subtitle : item.subtitle || ""} placeholder="Type the subtitle…"
                onChange={updateItem(i, "subtitle")} name={`${fieldName}[${i}].subtitle`} />
            </Col>
          </VariableArrayRow>
        ))}

        <Row className="mt-4">
          <Col md={{ offset: 2, size: 10 }}>
            <Button type="button" color="primary" onClick={addItem}>
              + Add section
            </Button>
          </Col>
        </Row>
      </div>
    </details>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
InfotextField.ownsChrome = true;
